import { useEffect, useRef, useState } from 'react';
import { monthlyPerformance } from '@/utils/monthlyPerformance';
import Reveal from '@/components/base/Reveal';

const formatSigned = (value: number): string =>
  `${value < 0 ? '-' : '+'}${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function MonthlyPerformance() {
  const results = monthlyPerformance.map((row) => row.netProfit);
  const best = Math.max(...results);
  const weakest = Math.min(...results);

  const tradeCounts = monthlyPerformance.map((row) => row.trades);
  const maxTrades = Math.max(...tradeCounts);
  const minTrades = Math.min(...tradeCounts);

  const first = monthlyPerformance[0];
  const last = monthlyPerformance[monthlyPerformance.length - 1];

  // Tracks which month's detail card is open. A tap/click on a marker opens it,
  // tapping the same marker (or anywhere outside) closes it again.
  const [selected, setSelected] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selected) return undefined;
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setSelected(null);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelected(null);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [selected]);

  // Node diameter scales with how much activity the month held — the busiest
  // month reads biggest, the quietest smallest. Kept within a comfortable band
  // so even a single quiet month still reads as a real marker.
  const nodeSize = (trades: number): number => {
    const span = maxTrades - minTrades;
    const ratio = span > 0 ? (trades - minTrades) / span : 1;
    return 54 + ratio * 36;
  };

  const lastIndex = monthlyPerformance.length - 1;

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                MONTHLY PERFORMANCE
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px] max-w-[760px]">
                How The Record Unfolds, Month By Month.
              </h2>
            </div>
            <p className="text-[14px] leading-relaxed text-foreground-600 max-w-[420px] lg:pb-2">
              Every recorded trade is grouped by month — showing how much activity fell into each
              period and how that month closed out.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div
            ref={rootRef}
            className="mt-12 rounded-lg border border-background-300 bg-background-50 p-5 md:p-10"
          >
            {/* card header + legend */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <span className="text-[12px] font-data tracking-[0.18em] text-foreground-500">
                NET RESULT · TIMELINE OF THE RECORD
              </span>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-2 text-[11px] font-data tracking-wide text-foreground-500 whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground-300"></span>
                    <span className="w-3 h-3 rounded-full bg-foreground-400"></span>
                  </span>
                  SIZE = TRADES
                </span>
                <span className="inline-flex items-center gap-2 text-[11px] font-data tracking-wide text-foreground-500 whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-profit-500"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-loss-500"></span>
                  </span>
                  COLOUR = MONTH RESULT
                </span>
                <span className="inline-flex items-center gap-2 text-[11px] font-data tracking-wide text-primary-600 whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-cursor-line text-[13px]"></i>
                  </span>
                  TAP A MARKER FOR DETAIL
                </span>
              </div>
            </div>

            {/* the timeline rail */}
            <div
              className="mt-14 grid"
              style={{ gridTemplateColumns: `repeat(${monthlyPerformance.length}, minmax(0, 1fr))` }}
            >
              {monthlyPerformance.map((row, index) => {
                const positive = row.netProfit >= 0;
                const isBest = row.netProfit === best;
                const isWeakest = row.netProfit === weakest;
                const size = nodeSize(row.trades);
                const isOpen = selected === row.key;

                // Keep the detail card inside the card edges for the end markers.
                const popAlign =
                  index === 0
                    ? 'left-0'
                    : index === lastIndex
                      ? 'right-0'
                      : 'left-1/2 -translate-x-1/2';

                return (
                  <div key={row.key} className="relative flex flex-col items-center px-0.5">
                    {/* value block */}
                    <div className="flex h-[124px] flex-col items-center justify-end gap-2 pb-5 text-center">
                      {isBest && (
                        <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-accent-100 text-[11px] font-data tracking-wide text-accent-900 whitespace-nowrap">
                          <i className="ri-arrow-up-line text-[12px]"></i>
                          BEST MONTH
                        </span>
                      )}
                      {isWeakest && !isBest && (
                        <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-background-100 border border-background-200 text-[11px] font-data tracking-wide text-loss-500 whitespace-nowrap">
                          <i className="ri-arrow-down-line text-[12px]"></i>
                          WEAKEST
                        </span>
                      )}
                      <span
                        className={`font-data text-[14px] md:text-[18px] font-semibold whitespace-nowrap ${
                          positive ? 'text-profit-600' : 'text-loss-500'
                        }`}
                      >
                        {formatSigned(row.netProfit)}
                      </span>
                    </div>

                    {/* axis segment + node */}
                    <div className="relative flex w-full items-center justify-center">
                      <span className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-background-300"></span>
                      <button
                        type="button"
                        aria-label={`${row.month} ${row.year}: ${formatSigned(row.netProfit)}, ${row.trades} trades, ${row.winRate.toFixed(1)}% win rate`}
                        aria-expanded={isOpen}
                        style={{ width: `${size}px`, height: `${size}px` }}
                        onClick={() => setSelected((prev) => (prev === row.key ? null : row.key))}
                        className={`group relative z-10 flex items-center justify-center rounded-full font-data text-[12px] md:text-[13px] text-background-50 transition-transform duration-200 cursor-pointer ${
                          positive ? 'bg-profit-500' : 'bg-loss-500'
                        } ${isOpen ? 'scale-105' : 'hover:scale-105'}`}
                      >
                        <span
                          className={`absolute -inset-1.5 rounded-full border transition-colors duration-200 ${
                            isOpen
                              ? 'border-foreground-800/45'
                              : positive
                                ? 'border-profit-500/25'
                                : 'border-loss-500/25'
                          }`}
                        ></span>
                        {row.trades}
                      </button>

                      {/* detail card */}
                      <div
                        className={`absolute bottom-full z-30 mb-3 w-[212px] max-w-[72vw] transition duration-150 ${popAlign} ${
                          isOpen
                            ? 'translate-y-0 opacity-100'
                            : 'pointer-events-none translate-y-1 opacity-0'
                        }`}
                      >
                        <div className="rounded-lg border border-background-400 bg-background-50 p-3.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-heading text-[13px] font-bold text-foreground-950 whitespace-nowrap">
                              {row.month} {row.year}
                            </span>
                            <span
                              className={`font-data text-[12px] font-semibold whitespace-nowrap ${
                                positive ? 'text-profit-600' : 'text-loss-500'
                              }`}
                            >
                              {formatSigned(row.netProfit)}
                            </span>
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
                            <div>
                              <p className="text-[10px] font-data tracking-wide text-foreground-400">
                                TRADES
                              </p>
                              <p className="mt-0.5 font-data text-[13px] font-semibold text-foreground-950">
                                {row.trades}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-data tracking-wide text-foreground-400">
                                WIN RATE
                              </p>
                              <p className="mt-0.5 font-data text-[13px] font-semibold text-foreground-950">
                                {row.winRate.toFixed(1)}%
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-data tracking-wide text-foreground-400">
                                WINS
                              </p>
                              <p className="mt-0.5 font-data text-[13px] font-semibold text-profit-600">
                                {row.wins}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-data tracking-wide text-foreground-400">
                                LOSSES
                              </p>
                              <p className="mt-0.5 font-data text-[13px] font-semibold text-loss-500">
                                {row.losses}
                              </p>
                            </div>
                          </div>

                          {/* win / loss split */}
                          <div className="mt-3.5 h-1 rounded-full overflow-hidden bg-loss-500/30 flex">
                            <span
                              className="h-full bg-profit-500"
                              style={{ width: `${row.trades > 0 ? (row.wins / row.trades) * 100 : 0}%` }}
                            ></span>
                          </div>
                          <p className="mt-2 font-data text-[10px] tracking-wide text-foreground-400">
                            {row.wins}W · {row.losses}L
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* month + stats block */}
                    <div className="flex h-[96px] flex-col items-center gap-1 pt-5 text-center">
                      <span className="font-data text-[13px] md:text-[14px] text-foreground-950 whitespace-nowrap">
                        {row.month} {row.year}
                      </span>
                      <span className="font-data text-[11px] md:text-[12px] text-foreground-500 whitespace-nowrap">
                        {row.winRate.toFixed(1)}% win
                      </span>
                      <span className="font-data text-[11px] text-foreground-400 whitespace-nowrap">
                        {row.wins}W · {row.losses}L
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-10 text-[12px] leading-relaxed text-foreground-400">
              Each marker is one calendar month: its size reflects how many trades were recorded,
              its colour reflects whether the month closed up or down. Tap any marker to open its
              full breakdown. Monthly net result is the sum of recorded trade outcomes in that
              period. {first.short} — {last.short} {first.year} covers the recorded window.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default MonthlyPerformance;